import { isReachable } from '../../shared/lib/businessNumber.js';
import { isInSetup, type Availability } from '@acs/shared';
import { isSubscribed } from '../../shared/lib/subscription.js';

import type { BusinessDoc, ServiceDoc } from '../../shared/api/converters.js';
import type { TranslationKey } from '../../i18n/index.js';

/**
 * Setup-wizard progress.
 *
 * Only `business.onboardingCompletedAt` is persisted; *which* step is still
 * outstanding is derived from the real configuration every time. A stored step
 * counter would drift the moment an owner deleted their last service or their
 * number failed to provision — this can't.
 */

export type StepId =
  | 'profile'
  | 'services'
  | 'hours'
  | 'assistant'
  | 'trycall'
  | 'forwarding'
  | 'plan'
  | 'done';

export interface StepDef {
  id: StepId;
  /** Catalogue keys, not copy — this module is pure data (doc 20 D8). */
  labelKey: TranslationKey;
  /** One line under the step's heading — what this step is for. */
  blurbKey: TranslationKey;
}

/**
 * The wizard, in order. `done` is the celebration screen, not a task.
 *
 * ── Why THIS order ────────────────────────────────────────────────────────
 *
 * Two constraints pull against each other, and the order is where they are
 * reconciled.
 *
 * **Forwarding is not instant.** Diverting a line means dialling a carrier code
 * — and for some owners, ringing their operator and waiting days for it to be
 * enabled. It is the one step that can stall for reasons nobody here controls.
 * Charging a card before knowing whether that line can be connected at all is
 * the wrong way round: the owner would be paying for a service that may never
 * reach them. So `forwarding` comes BEFORE `plan`, and the money is asked for
 * only once the divert has been proven to arrive.
 *
 * **But a connected line must not answer for free.** Putting forwarding first
 * opens a window where a real diverted call arrives for a business nobody has
 * paid for. That is closed in the gateway, not here: `serviceBlockReason`
 * returns `setup_unpaid` and the call is refused before any model is involved,
 * so the line is connected but inert until the plan is paid. Doing it that way
 * round — rather than withholding the dial codes — is what lets the owner sort
 * out their operator at their own pace.
 *
 * `trycall` sits between the assistant and forwarding, and is OPTIONAL: it is
 * the browser test call, so the owner can hear the thing they are about to wire
 * their phone into. It never blocks (its `done` is always true), because an
 * owner without a microphone, or one who simply does not want to, must not be
 * stuck. It is also free of the paywall by design — the owner's own test calls
 * never consume an allowance (doc 26 D3), which is what makes "try it before
 * you pay" honest rather than a demo of something they cannot have.
 *
 * `profile` is business facts only. Everything about how the assistant *sounds*
 * lives in `assistant`, which sits after the menu and hours: there is no point
 * tuning a greeting for a business that cannot yet say what it sells.
 */
export const STEPS: StepDef[] = [
  { id: 'profile', labelKey: 'steps.profile.label', blurbKey: 'steps.profile.blurb' },
  { id: 'services', labelKey: 'steps.services.label', blurbKey: 'steps.services.blurb' },
  { id: 'hours', labelKey: 'steps.hours.label', blurbKey: 'steps.hours.blurb' },
  { id: 'assistant', labelKey: 'steps.assistant.label', blurbKey: 'steps.assistant.blurb' },
  { id: 'trycall', labelKey: 'steps.trycall.label', blurbKey: 'steps.trycall.blurb' },
  {
    id: 'forwarding',
    labelKey: 'steps.forwarding.label',
    // Not "get a number": the owner already has one and keeps it. Ours is a
    // forwarding target they dial once (doc 18 §7.5).
    blurbKey: 'steps.forwarding.blurb',
  },
  { id: 'plan', labelKey: 'steps.plan.label', blurbKey: 'steps.plan.blurb' },
  { id: 'done', labelKey: 'steps.done.label', blurbKey: 'steps.done.blurb' },
];

/** Index of a step id within {@link STEPS}. */
export function stepIndex(id: StepId): number {
  return STEPS.findIndex((s) => s.id === id);
}

export interface OnboardingInput {
  business: BusinessDoc;
  services: ServiceDoc[] | undefined;
  availability: Availability | null | undefined;
}

export interface OnboardingProgress {
  /** Completion of the real steps (everything but `done`), in STEPS order. */
  done: boolean[];
  /** Index of the first outstanding step — or the `done` screen when there is none. */
  firstUnfinished: number;
  /** Every task step satisfied (the owner may still need to press Finish). */
  ready: boolean;
}

export function onboardingProgress({
  business,
  services,
  availability,
}: OnboardingInput): OnboardingProgress {
  const done = [
    // Business facts. Both are collected by the creation page — which IS this
    // step — so a business that exists has already satisfied it. The step stays
    // in the rail and stays revisitable; it is simply never the thing blocking
    // an owner, which is the honest reading of "you already told us this".
    business.name.trim().length > 0,
    (services ?? []).some((s) => s.active),
    Object.values(availability?.hours ?? {}).some((intervals) => (intervals?.length ?? 0) > 0),
    // The greeting is the one thing the owner must actively write; language has
    // a country-derived default.
    business.greeting.trim().length > 0,
    // The optional test call. ALWAYS satisfied: it is an invitation, not a
    // task, and an owner with no microphone — or no wish to talk to a computer
    // — must not be held here. Nothing about a business changes by taking it.
    true,
    // Doc 18 §7.3: "done" here means the business can actually receive calls,
    // which on a SHARED number requires a proven line and on a dedicated one
    // does not. `isReachable` is the single statement of that rule — the same
    // one the dashboard banner and `completeOnboarding` enforce, so the three
    // cannot drift into telling an owner different things.
    isReachable(business),
    // Every business must be on a plan — a trial counts, because a trial goes
    // through Checkout and therefore sets one (doc 26 §3a). `isSubscribed`
    // rather than a status equality so `past_due` stays finished: a card that
    // failed last night must not drag an owner back into setup.
    //
    // UNCONDITIONAL. This used to sit behind `VITE_REQUIRE_SUBSCRIPTION`, which
    // ships as `false` — so the step counted as DONE for everyone and
    // `firstUnfinished` walked straight past it. A build flag deciding whether
    // money is required is the wrong shape of switch; what remains behind a
    // flag is only whether the GATEWAY cuts off businesses that are already
    // live (doc 26 §5).
    isSubscribed(business),
  ];
  const firstUnfinished = done.indexOf(false);
  return {
    done,
    firstUnfinished: firstUnfinished === -1 ? stepIndex('done') : firstUnfinished,
    ready: firstUnfinished === -1,
  };
}

/**
 * Whether this business should be held in the setup wizard.
 *
 * A thin alias for `isInSetup` in `@acs/shared`, which is where the rule itself
 * lives — and it must live in one place now that two very different things read
 * it: this gate, and the GATEWAY, which refuses a diverted call for a business
 * that is still in setup and unpaid (`serviceBlockReason` → `setup_unpaid`).
 * Two copies of "is setup finished" would mean a line the portal calls live and
 * the phone refuses, or worse, the other way round.
 *
 * Businesses that predate the wizard have no completion stamp, so anything
 * already live (or retired) is grandfathered in — we never drag a working
 * receptionist back through setup, and never silence one.
 */
export function needsOnboarding(business: BusinessDoc): boolean {
  return isInSetup(business);
}
